import React from 'react';
import { Button, Table, Typography } from 'antd';
import moment from 'moment';
import {
  ACTIVE,
  BUY,
  CALL_OPTION,
  OPTION_CONTRACTS_STATUS,
  OPTION_CONTRACTS_TYPES,
  PUT_OPTION,
  REGIONS,
  RISK_APPROACHES,
  selectors,
  SELL,
  TRANSACTION_TYPES,
} from 'state';
import { useDispatch, useSelector } from 'react-redux';
import type { TableColumnsType as ColumnsType } from 'antd';
import { OptionsChainRow } from './OptionsChain';
import data from '../../AdminDashboard/Data';
import {
  optionsInstruments,
  optionsInstrumentsList,
} from '../../../state/app/selectors';
import { formatters } from 'services';
import { columnSearchWidget } from 'components';
import { PlayerOptionsContractType } from '../../../state/app/options/types';

type EnrichedPlayerOptionsContractType = PlayerOptionsContractType & {
  asset_name: string;
};

const OptionsBlotter: React.FC = () => {
  const dispatch = useDispatch();
  const player = useSelector(selectors.player);
  const instruments = useSelector(selectors.optionsInstruments);
  const assets = useSelector(selectors.underlyingAssets);
  const currencies = useSelector(selectors.currencies);
  const session = useSelector(selectors.activeSession);
  const allContracts: EnrichedPlayerOptionsContractType[] = Object.values(
    instruments
  ).flatMap(instr => {
    // grab the asset name once per instrument
    const assetName = assets[instr.options_asset].name;
    const currency = currencies[assets[instr.options_asset].base_currency];
    // enrich each contract with that name
    return Object.values(instr.options_contracts).map(contract => ({
      ...contract,
      asset_name: assetName,
      currency: currency.symbol,
    }));
  });

  const nonActiveContracts = allContracts.filter(
    contract => contract.status !== ACTIVE
  );
  const sortedContracts = nonActiveContracts.sort((a, b) => {
    const ta = new Date(a.closing_timestamp).getTime();
    const tb = new Date(b.closing_timestamp).getTime();
    return tb - ta;
  });

  const columns = [
    {
      title: 'Asset',
      dataIndex: 'asset_name',
      key: 'asset_name',
      // render: (val: number, row: PlayerOptionsContractType) => {
      //   const optionInstrument = instruments[row.options_instrument];
      //   const optionAsset = assets[optionInstrument.options_asset];
      //   return optionAsset.name;
      // },
      // sorter: (
      //   a: EnrichedPlayerOptionsContractType,
      //   b: EnrichedPlayerOptionsContractType
      // ) => a.asset_name.localeCompare(b.asset_name),
      ...columnSearchWidget({ dataIndex: 'asset_name' }),
      width: 50,
    },
    {
      title: 'Txn',
      dataIndex: 'transaction',
      key: 'transaction',
      render: (val: number) => TRANSACTION_TYPES[val],
      width: 15,
    },
    {
      title: 'Type',
      dataIndex: 'contract_type',
      key: 'contract_type',
      render: (val: number) => OPTION_CONTRACTS_TYPES[val],
      width: 20,
    },
    // {
    //   title: 'Maturity',
    //   dataIndex: 'quote_date',
    //   key: 'quote_date',
    //   render: (val: string, row: PlayerOptionsContractType) => {
    //     const scenarioDate = moment(session.scenario_date, 'YYYY-MM-DD');
    //     const expirationDate = moment(row.expiration_date, 'YYYY-MM-DD');
    //
    //     // diff in days (expirationDate - quoteDate)
    //     return expirationDate.diff(scenarioDate, 'days');
    //   },
    //   width: 30,
    // },
    {
      title: 'Premium',
      dataIndex: 'premium',
      key: 'premium',
      width: 40,
      render: formatters.commasNoDigits,
    },
    {
      title: 'CCY',
      dataIndex: 'currency',
      key: 'currency',
      width: 15,
    },
    { title: 'Strike', dataIndex: 'strike', key: 'strike', width: 25 },
    {
      title: 'Close Spot',
      dataIndex: 'close_spot',
      key: 'close_spot',
      width: 25,
      render: (val: number, row: PlayerOptionsContractType) => {
        return formatters.commas2Digits(val);
      },
    },
    // {
    //   title: 'Bid',
    //   dataIndex: 'current_bid',
    //   key: 'bid',
    //   width: 25,
    //   render: formatters.commas2Digits,
    // },
    // {
    //   title: 'Ask',
    //   dataIndex: 'current_ask',
    //   key: 'ask',
    //   width: 25,
    //   render: formatters.commas2Digits,
    // },
    {
      title: 'Open',
      dataIndex: 'open_price',
      key: 'open_price',
      width: 25,
      render: formatters.commas2Digits,
    },
    {
      title: 'Close',
      dataIndex: 'close_price',
      key: 'close_price',
      width: 25,
      render: (val: number | null, row: PlayerOptionsContractType) => {
        if (val !== null) return formatters.commas2Digits(val);
        return '-';
      },
    },
    {
      title: 'Contracts',
      dataIndex: 'contracts',
      key: 'contracts',
      render: (val: number, row: PlayerOptionsContractType) => {
        if (row.transaction === BUY) return formatters.commasNoDigits(val);
        return formatters.commasNoDigits(-val);
      },
      width: 40,
    },
    {
      title: 'Equity eq',
      dataIndex: 'contracts',
      key: 'equity_eq',
      render: (val: number, row: PlayerOptionsContractType) => {
        console.log(val);
        if (row.transaction === BUY)
          return formatters.commasNoDigits(val * 100);
        return formatters.commasNoDigits(-val * 100);
      },
      width: 40,
    },
    {
      title: 'IV Open',
      dataIndex: 'open_iv',
      key: 'open_iv',
      width: 27,
      render: formatters.percent2Digit,
    },
    // {
    //   title: 'IV close',
    //   dataIndex: 'current_iv',
    //   key: 'current_iv',
    //   width: 27,
    //   render: formatters.percent2Digit,
    // },
    {
      title: 'PnL',
      dataIndex: 'pnl',
      key: 'pnl',
      render: formatters.commas1Digit,
      width: 40,
    },
    {
      title: 'Intrinsic Value',
      dataIndex: 'intrinsic_pnl',
      key: 'intrinsic_pnl',
      render: (val: number, row: PlayerOptionsContractType) => {
        return formatters.commas1Digit(val);
      },
      width: 40,
    },
    // {
    //   title: 'Delta (stocks)',
    //   dataIndex: 'current_delta',
    //   key: 'delta_stocks',
    //   render: (val: number, row: PlayerOptionsContractType) => {
    //     return formatters.commasNoDigits(val * row.contracts * 100);
    //   },
    //   width: 40,
    // },
    // {
    //   title: 'Delta Hedge',
    //   dataIndex: 'contracts',
    //   key: 'delta_hedge',
    //   render: (val: number, row: PlayerOptionsContractType) => {
    //     return '-';
    //   },
    //   width: 40,
    // },
    {
      title: 'Delta Hedge PnL',
      dataIndex: 'delta_hedge_pnl',
      key: 'delta_hedge_pnl',
      render: (val: number, row: PlayerOptionsContractType) => {
        return formatters.commas1Digit(val);
      },
      width: 50,
    },
    {
      title: 'Overall PnL',
      dataIndex: 'overall_pnl',
      key: 'overall_pnl',
      render: (val: number, row: PlayerOptionsContractType) => {
        return formatters.commas1Digit(val);
      },
      width: 40,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val: number, row: PlayerOptionsContractType) => {
        return OPTION_CONTRACTS_STATUS[val];
      },
      width: 40,
    },
  ];

  // const expandedRowRender = (record: OptionsInstrumentType) => {
  //   const deduped = (() => {
  //     const seen = new Map<number, PlayerOptionsContractType>();
  //     for (const c of Object.values(record.options_contracts)) {
  //       if (!seen.has(c.id)) {
  //         seen.set(c.id, c);
  //       }
  //     }
  //     return Array.from(seen.values());
  //   })();
  //   const activeContracts = deduped.filter(item => item.status === ACTIVE);
  //   const sortedContracts = activeContracts.sort((a, b) => {
  //     const ta = new Date(a.timestamp).getTime();
  //     const tb = new Date(b.timestamp).getTime();
  //     return tb - ta;
  //   });

  // const contracts = ...
  //   return (
  //     <Table
  //       style={{ padding: '20px' }}
  //       columns={columns}
  //       dataSource={sortedContracts}
  //       pagination={false}
  //       rowKey="id"
  //       scroll={{ y: '300px' }}
  //       // footer={() => <div></div>}
  //     />
  //   );
  // };

  return (
    <div>
      <Table
        columns={columns}
        dataSource={sortedContracts}
        // expandedRowRender={expandedRowRender}
        rowKey="id"
        pagination={false}
        bordered
      />
    </div>
  );
};
export default OptionsBlotter;
